// src/components/Terms.jsx

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";

export default function Terms() {
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  return (
    <div style={pageStyle}>
      {/* Modern Navigation */}
      <nav style={navStyle}>
        <div style={navContainerStyle}>
          <span style={navLogoStyle} onClick={() => navigate("/")} role="button">
            Dansy
          </span>
          
          <div style={navButtonsStyle}>
            <button onClick={() => navigate("/")} style={navBackButton}>
              ← Back to Home
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div style={heroStyle}>
        <div style={heroContentStyle}>
          <h1 style={heroTitleStyle} data-aos="fade-down">Terms of Service</h1>
          <p style={heroSubtitleStyle} data-aos="fade-up">
            Please read these terms carefully before using Dansy
          </p>
          <p style={lastUpdatedStyle}>Last updated: November 4, 2025</p>
        </div>
      </div>

      {/* Content */}
      <div style={contentContainerStyle}>
        <div style={contentWrapperStyle} data-aos="fade-up">
          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>1. Purpose</h2>
            <p style={paragraphStyle}>
              These Terms of Service ("Terms") govern access to and use of the Dansy platform, provided by Dansy. By using this platform, you accept these Terms in their entirety. If you do not accept these Terms, please do not use the platform.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>2. Access to Service</h2>
            <p style={paragraphStyle}>
              Access to the platform is free and open to all. However, Dansy reserves the right to modify or suspend, temporarily or permanently, access to all or part of the platform, without prior notice.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>3. User Obligations</h2>
            <p style={paragraphStyle}>
              Users agree to use the platform in compliance with applicable laws and not to disrupt its operation. Any abusive or unauthorized use may result in legal action.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>4. Intellectual Property</h2>
            <p style={paragraphStyle}>
              All content on the platform (text, images, logos, etc.) is protected by intellectual property rights. Any reproduction, modification, or distribution without prior authorization is prohibited.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>5. Limitation of Liability</h2>
            <p style={paragraphStyle}>
              Dansy cannot be held liable for direct or indirect damages resulting from access to or use of the platform. Users are solely responsible for their use of the information provided.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>6. Changes to Terms</h2>
            <p style={paragraphStyle}>
              We reserve the right to modify these Terms at any time. Changes will take effect upon publication on the platform. It is recommended to regularly review this page to be aware of any updates.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>7. Applicable Law and Jurisdiction</h2>
            <p style={paragraphStyle}>
              These Terms are governed by French law. Any dispute relating to their interpretation or execution shall be subject to the exclusive jurisdiction of French courts.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>8. Contact</h2>
            <p style={paragraphStyle}>
              For any questions or requests for information regarding these Terms, please contact us at: <a href="mailto:contact@dansy.net" style={linkStyle}>contact@dansy.net</a>.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>9. Acceptance of Terms</h2>
            <p style={paragraphStyle}>
              By accessing and using the platform, you declare that you have read, understood, and accept without reservation all of these Terms of Service.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>10. Responsibilities</h2>
            <p style={paragraphStyle}>
              Users are solely responsible for their actions on the platform. Dansy disclaims all liability for direct or indirect damages caused by the use or inability to use the platform, including data loss.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>11. Duration and Termination</h2>
            <p style={paragraphStyle}>
              These Terms come into effect upon first use of the platform and remain applicable until terminated by either party. Dansy reserves the right to suspend or terminate access to the platform for any user who does not comply with these Terms.
            </p>
          </section>

          <section style={sectionStyle}>
            <h2 style={sectionTitleStyle}>12. Force Majeure</h2>
            <p style={paragraphStyle}>
              Dansy cannot be held liable in case of partial or total non-performance of its obligations if such non-performance is due to force majeure such as natural disasters, armed conflict, or any other circumstance beyond Dansy's reasonable control.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

/*--------------------------------
  STYLES
---------------------------------*/

const pageStyle = {
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  backgroundColor: "#fafafa",
  minHeight: "100vh",
};

const navStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1000,
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 2px 20px rgba(0,0,0,0.08)',
};

const navContainerStyle = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '20px 40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const navLogoStyle = {
  fontSize: '1.5rem',
  fontFamily: "'Shirkhand', cursive",
  color: '#ff477e',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
};

const navButtonsStyle = {
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
};

const navBackButton = {
  padding: '10px 24px',
  fontSize: '0.95rem',
  backgroundColor: 'transparent',
  color: '#333',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 500,
  transition: 'background-color 0.2s ease',
};

const heroStyle = {
  background: 'linear-gradient(135deg, #ff477e 0%, #ff6b9d 100%)',
  padding: '140px 40px 80px',
  textAlign: 'center',
  color: 'white',
};

const heroContentStyle = {
  maxWidth: '800px',
  margin: '0 auto',
};

const heroTitleStyle = {
  fontSize: '3.5rem',
  fontWeight: 700,
  marginBottom: '16px',
};

const heroSubtitleStyle = {
  fontSize: '1.25rem',
  marginBottom: '16px',
  opacity: 0.95,
};

const lastUpdatedStyle = {
  fontSize: '0.9rem',
  opacity: 0.8,
};

const contentContainerStyle = {
  maxWidth: '800px',
  margin: '-60px auto 0',
  padding: '0 40px 100px',
  position: 'relative',
  zIndex: 2,
};

const contentWrapperStyle = {
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '60px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
};

const sectionStyle = {
  marginBottom: '48px',
};

const sectionTitleStyle = {
  fontSize: '1.8rem',
  fontWeight: 700,
  color: '#ff477e',
  marginBottom: '16px',
};

const paragraphStyle = {
  fontSize: '1.05rem',
  lineHeight: 1.8,
  color: '#666',
};

const linkStyle = {
  color: '#ff477e',
  textDecoration: 'none',
  fontWeight: 500,
  borderBottom: '1px solid rgba(255, 71, 126, 0.3)',
  transition: 'border-color 0.2s ease',
};
